import { useState } from 'react'
import { notifications } from '@mantine/notifications'
import { IconHourglass, IconPlayerPlay, IconSearch } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatApiError } from '@/api/errors'
import { api } from '@/api/client'
import { invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'

export interface ChamadoSummary {
  id: number
  status: string
  assignee: { id: number; full_name: string } | null
  category?: { id: number; name: string } | null
  priority?: string
  request_type?: string
  current_environment?: string | null
  requested_by_user_id?: number | null
  /** Quem iniciou a triagem nesta chegada (evento `triagem_iniciada`), ou null. */
  triagem_iniciada_por_nome?: string | null
  /** A triagem foi concluída nesta chegada (evento `triagem_concluida`). */
  triagem_concluida?: boolean
}

/**
 * Entrada Mailbox §6.1: o chamado nasce em `in_triage`, e "Triagem em execução" é o evento
 * `triagem_iniciada`. O botão Iniciar Triagem aparece também em `in_triage` sem o evento, para
 * quem pode atribuir (`chamados:assign`) e não é o solicitante. Quem decide é o backend (403).
 */
export function mostraIniciarTriagem(
  chamado: Pick<ChamadoSummary, 'status' | 'requested_by_user_id' | 'triagem_iniciada_por_nome'>,
  quem: { userId: number | null | undefined; podeAtribuir: boolean },
): boolean {
  if (chamado.status !== 'in_triage' || chamado.triagem_iniciada_por_nome) return false
  if (!quem.podeAtribuir || quem.userId == null) return false
  return quem.userId !== chamado.requested_by_user_id
}

/**
 * Spec 2026-10-21 Entrada por etapa da triagem §4.5: com a triagem iniciada e ainda não
 * concluída, "Concluir triagem" passa o chamado da pasta Triagem para "Do meu time, sem
 * responsável". A mesma régua de quem pode iniciar; quem decide é o backend (403/409).
 */
export function mostraConcluirTriagem(
  chamado: Pick<ChamadoSummary, 'status' | 'requested_by_user_id' | 'triagem_iniciada_por_nome' | 'triagem_concluida'>,
  quem: { userId: number | null | undefined; podeAtribuir: boolean },
): boolean {
  if (chamado.status !== 'in_triage' || !chamado.triagem_iniciada_por_nome || chamado.triagem_concluida) return false
  if (!quem.podeAtribuir || quem.userId == null) return false
  return quem.userId !== chamado.requested_by_user_id
}

// "Reiniciar fluxo" (admin, spec 2026-10-14, consoleai#24954): volta para a triagem no
// ambiente de origem. Fica fora de chamado encerrado (Reabrir), resolvido (validação do
// solicitante), já em triagem e GMUD (fluxo próprio). Quem decide de verdade é o backend.
export function podeReiniciarFluxo(chamado: Pick<ChamadoSummary, 'status' | 'request_type' | 'current_environment'>): boolean {
  if (['closed', 'cancelled', 'rejected', 'resolved', 'requested', 'in_triage'].includes(chamado.status)) return false
  if (chamado.status.startsWith('gmud') || chamado.request_type === 'gmud') return false
  return chamado.current_environment !== 'prd'
}

// Transicoes disponiveis para atendentes/admins por status atual. Sem "Resolver" (D5 da
// spec 2026-09-27): ele pulava a validacao do solicitante; encerrar pelo atendente e
// "Finalizar atendimento". A transicao in_progress -> resolved segue no backend (API e chat).
// Sem cor própria: a variante sai da posição na faixa (DS §6.1, spec 2026-10-21 §3.4).
export const ATENDENTE_TRANSITIONS: Record<string, Array<{
  to: string
  label: string
  icon: React.ReactNode
}>> = {
  // Chamado encaminhado volta para 'requested' (fila do novo time) e, sem esta
  // entrada, ficava sem botão nenhum: o atendente não tinha como tirá-lo da fila.
  requested: [
    { to: 'in_triage', label: 'Iniciar Triagem', icon: <IconSearch size={14} /> },
  ],
  in_triage: [
    { to: 'in_progress', label: 'Iniciar Atendimento', icon: <IconPlayerPlay size={14} /> },
  ],
  in_progress: [
    { to: 'waiting_customer', label: 'Aguardar o cliente', icon: <IconHourglass size={14} /> },
  ],
  waiting_customer: [
    { to: 'in_progress', label: 'Retomar Atendimento', icon: <IconPlayerPlay size={14} /> },
  ],
  reopened: [
    { to: 'in_progress', label: 'Retomar Atendimento', icon: <IconPlayerPlay size={14} /> },
  ],
}

// Status em que as ações v2 do atendente ficam disponíveis.
const RECLASSIFY_STATUSES = ['requested', 'in_triage', 'in_progress']

interface TicketCategory {
  id: number
  name: string
  default_request_type?: string | null
}

interface UserOption {
  id: number
  full_name: string
  email: string
  is_active: number
}

interface AssignableUsersResponse {
  items: UserOption[]
  target_template_id: number | null
  target_template_name: string | null
  include_outside_team: boolean
}

export interface AcoesDoChamadoProps {
  chamado: ChamadoSummary
  tenantId: number
  isAdmin: boolean
  /** Quem olha e se pode atribuir (`chamados:assign`): decidem o botão Iniciar Triagem. */
  userId?: number | null
  podeAtribuir?: boolean
  /**
   * Quem vê as ações do atendente (`chamados:assign`, `chamados:approve` ou admin): o mesmo
   * gate que antes decidia se o painel de triagem renderizava. Desligado, o hook não consulta
   * nada e não oferece ação nenhuma.
   */
  habilitado?: boolean
}

/**
 * As ações do atendente sobre um chamado — estado, mutações e quem vê cada uma. A faixa
 * Próximo passo e o rodapé do cartão de leitura (spec 2026-10-21 §3.4, §3.5) consomem o mesmo
 * hook, para nenhuma mutação existir duas vezes. As regras de visibilidade são as do antigo
 * `TriagePanel`, condição por condição.
 */
export function useAcoesDoChamado({
  chamado, tenantId, isAdmin, userId, podeAtribuir = false, habilitado = true,
}: AcoesDoChamadoProps) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')
  const [pendingTransition, setPendingTransition] = useState<string | null>(null)
  // Toggle admin (Modelo A): amplia listagem pra todos users ativos do tenant.
  const [includeOutsideTeam, setIncludeOutsideTeam] = useState(false)

  const [activeAction, setActiveAction] = useState<'reclassify' | 'block' | null>(null)
  const [reCategory, setReCategory] = useState<string | null>(
    chamado.category ? String(chamado.category.id) : null,
  )
  const [rePriority, setRePriority] = useState<string | null>(chamado.priority ?? null)
  const [reRequestType, setReRequestType] = useState<string | null>(chamado.request_type ?? null)
  const [reNote, setReNote] = useState('')
  const [blockedUntil, setBlockedUntil] = useState<Date | null>(null)
  const [blockNote, setBlockNote] = useState('')
  const [restartOpen, setRestartOpen] = useState(false)
  const [restartReason, setRestartReason] = useState('')

  const basePath = `/v1/tenants/${tenantId}/workflow/${chamado.id}`
  const isClosed = ['closed', 'rejected'].includes(chamado.status)
  // O painel antigo não renderizava para quem não é atendente, nem em chamado encerrado
  // para quem não é admin.
  const visivel = habilitado && !(isClosed && !isAdmin)
  const aberto = visivel && !isClosed
  const canReclassify = RECLASSIFY_STATUSES.includes(chamado.status)
  const canBlockOrFinalize = chamado.status === 'in_progress'

  const pode = {
    seletor: aberto,
    iniciarTriagem: visivel && mostraIniciarTriagem(chamado, { userId, podeAtribuir }),
    concluirTriagem: visivel && mostraConcluirTriagem(chamado, { userId, podeAtribuir }),
    finalizar: aberto && canBlockOrFinalize,
    reclassificar: aberto && canReclassify,
    // O "Encaminhar" do painel: hoje sai pelo do rodapé, que é o mesmo `encaminhar`.
    encaminhar: aberto && canReclassify,
    bloquear: aberto && canBlockOrFinalize,
    reiniciar: visivel && isAdmin && podeReiniciarFluxo(chamado),
    // Só `closed`: `rejected→reopened` não existe, e o botão daria transição inválida.
    reabrir: visivel && isAdmin && chamado.status === 'closed',
    fechar: visivel && isAdmin && !isClosed && chamado.status !== 'resolved',
  }
  const transitions = visivel ? ATENDENTE_TRANSITIONS[chamado.status] ?? [] : []

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['chamado', String(chamado.id)] })
    invalidarPendenciasDoChamado(queryClient)
  }

  // Modelo A: lista users do time destinatario no tenant do chamado. Toggle
  // admin (?include_outside_team=true) amplia pra todos ativos do tenant —
  // backend ignora silenciosamente o flag pra nao-admins.
  const { data: assignableData, isLoading: loadingAssignable } = useQuery<AssignableUsersResponse>({
    queryKey: ['assignable-users', tenantId, chamado.id, includeOutsideTeam],
    queryFn: async () => {
      const res = await api.get<AssignableUsersResponse>(
        `${basePath}/assignable-users${includeOutsideTeam ? '?include_outside_team=true' : ''}`,
      )
      return res.data
    },
    enabled: pode.seletor,
    staleTime: 60_000,
  })

  const users = assignableData?.items ?? []
  const userOptions = users.map((u) => ({
    value: String(u.id),
    label: u.full_name || u.email,
  }))

  const assignMutation = useMutation({
    mutationFn: async (assigneeUserId: number | null) => {
      await api.patch(`${basePath}/assign`, { assignee_user_id: assigneeUserId })
    },
    onSuccess: () => {
      invalidate()
      notifications.show({ color: 'green', message: 'Atendente atualizado.' })
    },
    onError: (err: unknown) => {
      // Mapeia codigos 422 do backend (Modelo A) pra mensagens legiveis.
      const raw = formatApiError(err, 'Falha ao atribuir atendente.')
      const friendly: Record<string, string> = {
        assignee_outside_team: 'Esse usuário não faz parte do time deste chamado. Use o toggle "Fora do time" (admin) ou encaminhe o chamado pra outro time.',
        assignee_not_in_tenant: 'Esse usuário não tem acesso ao tenant deste chamado.',
        assignee_inactive: 'Esse usuário esta inativo.',
        assignee_not_found: 'Usuário não encontrado.',
      }
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: friendly[raw] ?? raw,
      })
    },
  })

  // Iniciar Triagem sem mudar o status (in_triage sem o evento): grava `triagem_iniciada`.
  const iniciarTriagemMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/v1/chamados/${chamado.id}:iniciar-triagem`)
    },
    onSuccess: () => {
      invalidate()
      notifications.show({ color: 'green', message: 'Triagem iniciada.' })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao iniciar a triagem.') })
    },
  })

  // Concluir triagem (§4.3) e, se pedido, atribuir a quem clicou pela mesma rota de
  // atribuição do painel (§4.4). A atribuição que falhar não desfaz a triagem concluída.
  const concluirTriagemMutation = useMutation({
    mutationFn: async (atribuirAMim: boolean) => {
      await api.post(`/v1/chamados/${chamado.id}:concluir-triagem`)
      if (!atribuirAMim) return { atribuido: false, erro: null as unknown }
      try {
        await api.patch(`${basePath}/assign`, { assignee_user_id: userId })
        return { atribuido: true, erro: null as unknown }
      } catch (erro) {
        return { atribuido: false, erro }
      }
    },
    onSuccess: ({ atribuido, erro }) => {
      invalidate()
      if (erro) {
        notifications.show({
          color: 'yellow',
          title: 'Triagem concluída',
          message: `A atribuição não passou: ${formatApiError(erro, 'falha ao atribuir')}. O chamado está em "Do meu time, sem responsável".`,
        })
        return
      }
      notifications.show({
        color: 'green',
        message: atribuido ? 'Triagem concluída e chamado atribuído a você.' : 'Triagem concluída: o chamado foi para o time.',
      })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao concluir a triagem.') })
    },
  })

  const statusMutation = useMutation({
    mutationFn: async (toStatus: string) => {
      await api.patch(`${basePath}/status`, {
        to_status: toStatus,
        note: note.trim() || undefined,
      })
    },
    onSuccess: () => {
      invalidate()
      setNote('')
      setPendingTransition(null)
      notifications.show({ color: 'green', message: 'Status atualizado.' })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao mudar o status.') })
    },
  })

  const restartMutation = useMutation({
    mutationFn: async () => {
      await api.post(`${basePath}/restart`, { note: restartReason.trim() })
    },
    onSuccess: () => {
      // Sem invalidar 'chamado-chain': o reinício pode mudar o tenant do chamado, e a
      // chave antiga refaria GET /tenants/{tenant antigo}/.../chain → 403. A chave da
      // cadeia inclui o tenant, então o chamado recarregado já busca a cadeia certa.
      invalidate()
      setRestartOpen(false)
      setRestartReason('')
      notifications.show({ color: 'green', message: 'Fluxo reiniciado: o chamado voltou para a triagem.' })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao reiniciar o fluxo.') })
    },
  })

  // Categorias do tenant — carregadas apenas quando a ação Reclassificar abre.
  const { data: categories = [] } = useQuery<TicketCategory[]>({
    queryKey: ['ticket-categories', tenantId],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/ticket-categories`)
      return Array.isArray(res.data) ? res.data : []
    },
    enabled: activeAction === 'reclassify',
    staleTime: 60_000,
  })
  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }))

  const closeAction = () => setActiveAction(null)

  const reclassifyMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`${basePath}/classificacao`, {
        category_id: reCategory ? Number(reCategory) : undefined,
        priority: rePriority ?? undefined,
        request_type: reRequestType ?? undefined,
        note: reNote.trim() || undefined,
      })
    },
    onSuccess: () => {
      invalidate()
      closeAction()
      setReNote('')
      notifications.show({ color: 'green', message: 'Chamado reclassificado.' })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao reclassificar.') })
    },
  })

  const blockMutation = useMutation({
    mutationFn: async () => {
      if (!blockedUntil) return
      await api.post(`${basePath}/bloquear`, {
        blocked_until: blockedUntil.toISOString(),
        note: blockNote.trim() || undefined,
      })
    },
    onSuccess: () => {
      invalidate()
      closeAction()
      setBlockedUntil(null)
      setBlockNote('')
      notifications.show({ color: 'blue', message: 'Chamado bloqueado.' })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao bloquear.') })
    },
  })

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      await api.post(`${basePath}/finalizar`, {})
    },
    onSuccess: () => {
      invalidate()
      notifications.show({ color: 'teal', message: 'Atendimento finalizado. Aguardando validação do solicitante.' })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao finalizar.') })
    },
  })

  // A primeira escolha abre a nota opcional; a confirmação é o botão Confirmar da nota (ou um
  // segundo clique na mesma transição, como antes). A faixa mostra um formulário de cada vez.
  const handleTransition = (toStatus: string) => {
    setActiveAction(null)
    setRestartOpen(false)
    if (pendingTransition === toStatus) statusMutation.mutate(toStatus)
    else setPendingTransition(toStatus)
  }
  const cancelarTransicao = () => { setPendingTransition(null); setNote('') }

  const abrir = (qual: 'reclassify' | 'block' | 'restart') => {
    cancelarTransicao()
    setActiveAction(qual === 'restart' ? null : qual)
    setRestartOpen(qual === 'restart')
  }

  return {
    chamado, tenantId, isAdmin, isClosed, pode, transitions,
    seletor: {
      assignableData, loadingAssignable, userOptions, includeOutsideTeam, setIncludeOutsideTeam,
      atribuir: (value: string | null) => assignMutation.mutate(value ? Number(value) : null),
      atribuindo: assignMutation.isPending,
    },
    iniciarTriagemMutation, concluirTriagemMutation, statusMutation, finalizeMutation,
    transicao: { pendente: pendingTransition, note, setNote, escolher: handleTransition, cancelar: cancelarTransicao },
    formulario: {
      ativo: activeAction, abrir, fechar: closeAction,
      reclassificar: {
        reCategory, setReCategory, rePriority, setRePriority, reRequestType, setReRequestType,
        reNote, setReNote, categoryOptions, mutation: reclassifyMutation,
      },
      bloquear: { blockedUntil, setBlockedUntil, blockNote, setBlockNote, mutation: blockMutation },
      reiniciar: {
        aberto: restartOpen, fechar: () => setRestartOpen(false), restartReason, setRestartReason,
        mutation: restartMutation,
      },
    },
  }
}

export type AcoesDoChamado = ReturnType<typeof useAcoesDoChamado>
